/**
 * ONE shared pricing schema used by BOTH the charger default-tariff editor
 * (views/TariffEditor.tsx) and the Pricing Profiles create/edit form
 * (pages/PricingProfilesPage.tsx). The only difference between the two flows is where
 * the resulting pricebook is saved — the form, fields, validation, serialization and
 * canonical pricebook shape are identical here.
 *
 * Canonical pricebook: { currency_code, effective_pricebook: RateRule[] } — exactly the
 * shape the backend tariff/profile endpoints consume. rate_base is a plain number.
 */
import type { RateRule, FeeType, Uom, Pricebook } from "@/lib/api";
import { localization, type Translate } from "@/app/localization";

export interface PricingFormValues {
  currency: string;
  rules: RateRule[];
}

// fee_type → allowed units of measure (the canonical set from the charger tariff form).
export const UOM_BY_FEE: Record<FeeType, Uom[]> = {
  charging: ["kwh", "hour"],
  idle: ["hour"],
  activation: ["session"],
};

// Rates are shown with "$" whatever the pricebook currency: pre-existing display,
// kept unchanged until the currency display is decided (LOC-405 report).
export const RATE_CURRENCY_SYMBOL = "$";

export function emptyPricingForm(): PricingFormValues {
  return { currency: "USD", rules: [] };
}

export function newRule(): RateRule {
  return { fee_type: "charging", uom: "kwh", rate_base: 0, is_tou: false,
           start_time: "", end_time: "", days: "", priority: 0 };
}

/** Deserialize a stored pricebook into editable form values (round-trip with toPricebook). */
export function pricebookToValues(pb?: Pricebook | null): PricingFormValues {
  const rules = (pb?.effective_pricebook ?? []).map((r) => ({
    fee_type: r.fee_type,
    uom: r.uom,
    rate_base: typeof r.rate_base === "string" ? parseFloat(r.rate_base) || 0 : (r.rate_base ?? 0),
    is_tou: !!r.is_tou,
    start_time: r.start_time ?? "",
    end_time: r.end_time ?? "",
    days: r.days ?? "",
    priority: r.priority ?? 0,
  }));
  return { currency: pb?.currency_code || "USD", rules };
}

/** Serialize form values to the canonical pricebook the backend stores (identical for
 *  the charger default tariff and a pricing profile). */
export function valuesToPricebook(v: PricingFormValues): Pricebook {
  return {
    currency_code: (v.currency || "USD").trim() || "USD",
    effective_pricebook: v.rules.map((r) => {
      const base: RateRule = {
        fee_type: r.fee_type,
        uom: r.uom,
        rate_base: Number(r.rate_base) || 0,
      };
      if (r.is_tou) {
        base.is_tou = true;
        base.start_time = r.start_time || "";
        base.end_time = r.end_time || "";
        base.days = r.days || "";
        base.priority = r.priority ?? 0;
      }
      return base;
    }),
  };
}

export function isPricebookFree(pb?: Pricebook | null): boolean {
  return !(pb?.effective_pricebook ?? []).some((r) => (Number(r.rate_base) || 0) > 0);
}

function timeToMinutes(t?: string): number | null {
  if (!t) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(t.trim());
  if (!m) return null;
  const h = +m[1], min = +m[2];
  if (h < 0 || h > 24 || min < 0 || min > 59 || (h === 24 && min !== 0)) return null;
  return h * 60 + min;
}

/** Identical validation for both flows. Returns an error message (in the active
 *  catalog language at call time) or null. */
export function validatePricingForm(v: PricingFormValues, t: Translate = localization.t): string | null {
  if (!v.currency || !v.currency.trim()) return t("billing.tariffs.error_currency_required");
  for (let i = 0; i < v.rules.length; i++) {
    const r = v.rules[i];
    const rule = i + 1;
    if (!UOM_BY_FEE[r.fee_type]?.includes(r.uom)) return t("billing.tariffs.error_invalid_unit", { rule, fee: r.fee_type });
    const rate = Number(r.rate_base);
    if (!Number.isFinite(rate) || rate < 0) return t("billing.tariffs.error_rate_negative", { rule });
    if (r.is_tou) {
      const s = timeToMinutes(r.start_time), e = timeToMinutes(r.end_time);
      if (s === null || e === null) return t("billing.tariffs.error_window_invalid", { rule });
      // To earlier than From is a valid overnight window (backend RuleMatcher wraps past
      // midnight, e.g. 22:00–06:00). Only a zero-length window is an error.
      if (e === s) return t("billing.tariffs.error_window_empty", { rule });
    }
  }
  // No two all-day (non-ToU) rules for the same fee+uom (they'd both always apply).
  const seen = new Set<string>();
  for (let i = 0; i < v.rules.length; i++) {
    const r = v.rules[i];
    if (r.is_tou) continue;
    const key = `${r.fee_type}|${r.uom}`;
    if (seen.has(key)) return t("billing.tariffs.error_all_day_conflict", { fee: r.fee_type });
    seen.add(key);
  }
  return null;
}

// "<fee>|<uom>" -> the rate enum of billing.tariffs.summary_rate.
const SUMMARY_RATE: Record<string, string> = {
  "charging|kwh": "charging_kwh",
  "charging|hour": "charging_hour",
  "idle|hour": "idle_hour",
  "activation|session": "activation_session",
};

/**
 * ONE shared price summary used everywhere a tariff is described (charger default,
 * profiles list, charger Applied Profiles, public guest + authenticated rate). Includes
 * idle and activation — never silently omits them. Returns e.g.
 * "$0.2/kWh + $1/hr idle + $0.5/session" or "Free", in the active catalog language
 * at call time.
 */
export function summarizePricebook(pb?: Pricebook | null, t: Translate = localization.t, symbol = RATE_CURRENCY_SYMBOL): string {
  const rules = pb?.effective_pricebook ?? [];
  if (!rules.length) return t("billing.tariffs.free");
  // Representative rate per (fee,uom): a non-ToU all-day rate wins; else the max window rate.
  const rep = new Map<string, number>();
  for (const r of rules) {
    const key = `${r.fee_type}|${r.uom}`;
    const rate = Number(r.rate_base) || 0;
    const cur = rep.get(key);
    if (cur === undefined || !r.is_tou || rate > cur) rep.set(key, rate);
  }
  const order = ["charging|kwh", "charging|hour", "idle|hour", "activation|session"];
  const keys = [...order.filter((k) => rep.has(k)), ...[...rep.keys()].filter((k) => !order.includes(k))];
  const parts: string[] = [];
  for (const key of keys) {
    const rate = rep.get(key)!;
    if (rate <= 0) continue;
    parts.push(t("billing.tariffs.summary_rate", {
      rate: SUMMARY_RATE[key] ?? "other",
      amount: `${symbol}${rate}`,
      uom: key.split("|")[1],
    }));
  }
  const hasTou = rules.some((r) => r.is_tou);
  if (!parts.length) return t("billing.tariffs.free");
  // " + " joins rates like a formula; it is a symbol, not a word.
  const summary = parts.join(" + ");
  return hasTou ? t("billing.tariffs.summary_time_based", { summary }) : summary;
}
